import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, History as HistoryIcon, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const TYPES = {
  all: "Alle Bewegungen",
  entnahme: "Entnahmen",
  zugang: "Zugänge",
  rueckbuchung: "Rückbuchungen",
  umlagerung: "Umlagerungen",
};

const LOCS = {
  all: "Alle Lager",
  main: "Hauptlager",
  buffer: "Zwischenlager",
  pickup: "Abhollager",
};

function typeBadge(type, delta) {
  const map = {
    entnahme: { label: "Entnahme", cls: "bg-red-100 text-red-700" },
    zugang: { label: "Zugang", cls: "bg-green-100 text-green-700" },
    rueckbuchung: { label: "Rückbuchung", cls: "bg-blue-100 text-blue-700" },
    umlagerung: { label: "Umlagerung", cls: "bg-purple-100 text-purple-700" },
  };
  const fallback = delta < 0 ? map.entnahme : map.zugang;
  return map[type] || fallback;
}

export default function History() {
  const [searchParams] = useSearchParams();
  const articleFilter = searchParams.get("article") || "";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [locFilter, setLocFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { limit: 500 };
    if (articleFilter) params.article_id = articleFilter;
    api
      .get("/movements", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [articleFilter]);

  const filtered = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    return items.filter((m) => {
      if (typeFilter !== "all" && (m.type || "") !== typeFilter) {
        if (typeFilter === "entnahme" && m.delta < 0 && !m.type) return true;
        if (typeFilter === "zugang" && m.delta > 0 && !m.type) return true;
        return false;
      }
      if (locFilter !== "all" && (m.location || "main") !== locFilter) return false;
      if (!qLow) return true;
      return (
        (m.article_name || "").toLowerCase().includes(qLow) ||
        (m.project || "").toLowerCase().includes(qLow) ||
        (m.user_email || "").toLowerCase().includes(qLow) ||
        (m.article_ean || "").toLowerCase().includes(qLow)
      );
    });
  }, [items, q, typeFilter, locFilter]);

  const exportCsv = () => {
    const header = ["Zeitpunkt", "Typ", "Artikel", "EAN", "Menge", "Projekt", "Benutzer", "Notiz"];
    const rows = filtered.map((m) => [
      new Date(m.at).toLocaleString("de-DE"),
      m.type || (m.delta < 0 ? "entnahme" : "zugang"),
      m.article_name || "",
      m.article_ean || "",
      m.delta,
      m.project || "",
      m.user_email || "",
      m.note || "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `historie_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-5" data-testid="history-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            BEWEGUNGS-HISTORIE · {filtered.length} EINTRÄGE
          </div>
          <h1 className="font-display text-4xl uppercase flex items-center gap-3">
            <HistoryIcon className="w-8 h-8" /> Historie
          </h1>
          {articleFilter && (
            <div className="mt-2 inline-flex items-center gap-2 text-sm">
              <span className="text-zinc-500">Gefiltert nach Artikel:</span>
              <span className="mono text-xs">{articleFilter.slice(0, 8)}…</span>
              <Link to="/historie" className="text-[#F97316] underline">
                × Filter entfernen
              </Link>
            </div>
          )}
        </div>
        <Button
          onClick={exportCsv}
          data-testid="export-history-csv"
          variant="outline"
          className="rounded-sm border-zinc-400 h-11"
        >
          <Download className="w-4 h-4 mr-2" /> CSV-Export
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche Artikel, Projekt, Benutzer, EAN…"
            data-testid="history-search-input"
            className="pl-10 rounded-sm border-zinc-400 h-11"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="rounded-sm border-zinc-400 h-11" data-testid="history-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {Object.entries(TYPES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locFilter} onValueChange={setLocFilter}>
          <SelectTrigger className="rounded-sm border-zinc-400 h-11" data-testid="history-loc-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {Object.entries(LOCS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ZEITPUNKT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">TYP</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ARTIKEL</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">LAGER</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">MENGE</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">PROJEKT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">BENUTZER</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">NOTIZ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center mono text-xs text-zinc-500">
                    LADEN…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-zinc-500 text-sm">
                    Keine Bewegungen gefunden.
                  </td>
                </tr>
              ) : (
                filtered.map((m) => {
                  const t = typeBadge(m.type, m.delta);
                  return (
                    <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 mono text-xs whitespace-nowrap">
                        {new Date(m.at).toLocaleString("de-DE")}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-sm mono text-[10px] uppercase tracking-wider ${t.cls}`}>
                          {t.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/artikel/${m.article_id}`}
                          className="font-medium hover:text-[#F97316]"
                        >
                          {m.article_name || "—"}
                        </Link>
                        {m.article_ean && (
                          <div className="mono text-[11px] text-zinc-500">{m.article_ean}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 mono text-xs">
                        {LOCS[m.location] || m.location_label || "Hauptlager"}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-mono-num font-semibold ${
                          m.delta > 0 ? "text-green-700" : "text-red-700"
                        }`}
                      >
                        {m.delta > 0 ? `+${m.delta}` : m.delta}
                      </td>
                      <td className="px-4 py-2.5 text-sm">{m.project || "—"}</td>
                      <td className="px-4 py-2.5 mono text-xs text-zinc-600">{m.user_email}</td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500 max-w-xs truncate">
                        {m.note || "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100">
          {loading ? (
            <div className="px-4 py-8 text-center mono text-xs text-zinc-500">LADEN…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-zinc-500 text-sm">
              Keine Bewegungen gefunden.
            </div>
          ) : (
            filtered.map((m) => {
              const t = typeBadge(m.type, m.delta);
              return (
                <div key={m.id} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`inline-block px-2 py-0.5 rounded-sm mono text-[10px] uppercase tracking-wider ${t.cls}`}>
                      {t.label}
                    </span>
                    <span
                      className={`font-mono-num font-bold text-base ${
                        m.delta > 0 ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {m.delta > 0 ? `+${m.delta}` : m.delta}
                    </span>
                  </div>
                  <Link
                    to={`/artikel/${m.article_id}`}
                    className="block font-medium text-sm hover:text-[#F97316]"
                  >
                    {m.article_name || "—"}
                  </Link>
                  <div className="text-xs text-zinc-500 mt-1">
                    {new Date(m.at).toLocaleString("de-DE")} · {LOCS[m.location] || "Hauptlager"} · {m.user_email}
                  </div>
                  {m.project && (
                    <div className="text-xs mt-1">
                      <span className="text-zinc-500">Projekt:</span>{" "}
                      <span className="font-medium">{m.project}</span>
                    </div>
                  )}
                  {m.note && (
                    <div className="text-xs text-zinc-500 mt-0.5">„{m.note}"</div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
